import React, { useEffect, useState } from "react";
import { getCookies } from "cookies-next";
import axios from "axios";
import Swal from "sweetalert2";
import BreadCrump from "../tools/breadcrump";
import HeaderOfPage from "../tools/headerOfPage";
import { useTranslation } from "react-i18next";
import { config } from "../../data/config";

interface LanguageCode {
    code: string;
    label: string;
}

export default function ContactPage() {
  const { t } = useTranslation();
  const cookie = getCookies();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeLang, setActiveLang] = useState<string>('en');
  const [languages, setLanguages] = useState<LanguageCode[]>([]);

  const inputStyle =
    "py-2 px-3 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm";

  const breadcrump = [
    { name: "Dashboard", url: "/dashboard" },
    { name: "Setting", url: "/dashboard/setting" },
    { name: "Contact", url: null },
  ];

  // Load languages
  const loadLanguages = async (): Promise<LanguageCode[]> => {
    try {
      const res = await axios.get(`${config.url}/v1/language`);
      const languageList = res.data.message || [];
      // Map backend language format to frontend format
      const mappedLanguages = languageList
        .filter((lang: any) => lang.active) // Only show active languages
        .sort((a: any, b: any) => (a.arranged || 0) - (b.arranged || 0)) // Sort by arranged order
        .map((lang: any) => ({
          code: lang.code,
          label: lang.label
        }));
      setLanguages(mappedLanguages);
      // Set default active language to first language or 'en'
      if (mappedLanguages.length > 0) {
        setActiveLang(mappedLanguages[0].code);
      }
      return mappedLanguages;
    } catch (error) {
      console.error('Error loading languages:', error);
      // Fallback to default languages if API fails
      const fallbackLanguages = [
        { code: 'ku', label: 'Kurdish' },
        { code: 'fa', label: 'فارسی' },
        { code: 'en', label: 'English' },
        { code: 'ar', label: 'العربية' },
        { code: 'tr', label: 'Türkçe' }
      ];
      setLanguages(fallbackLanguages);
      return fallbackLanguages;
    }
  };

  // Helper function to initialize multilingual field
  const initializeMultilingualField = (field: any, languageCodes: string[]): any => {
    if (!field) {
      const obj: any = {};
      languageCodes.forEach(code => obj[code] = '');
      return obj;
    }
    if (typeof field === 'string') {
      const obj: any = {};
      languageCodes.forEach(code => obj[code] = field);
      return obj;
    }
    if (typeof field === 'object') {
      // Ensure all language codes exist
      const obj: any = { ...field };
      languageCodes.forEach(code => {
        if (!obj[code]) obj[code] = '';
      });
      return obj;
    }
    const obj: any = {};
    languageCodes.forEach(code => obj[code] = '');
    return obj;
  };

  const getMultilingualValue = (field: any, lang: string): string => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    if (typeof field === 'object') {
      return field[lang] || '';
    }
    return '';
  };

  const load = async (languageCodes: string[] = []) => {
    try {
      setLoading(true);
      const res = await axios.get(`${config.url}/v1/dashboard/contact`, {
        headers: { Authorization: "Bearer " + cookie["token"] },
      });
      const loadedData = res.data.message;
      
      // Use provided language codes or defaults
      const codes = languageCodes.length > 0 
        ? languageCodes
        : ['ku', 'fa', 'en', 'ar', 'tr'];
      
      // Normalize multilingual fields - ensure all fields are objects
      if (loadedData) {
        const multilingualFields = ['laddress1', 'address1', 'laddress2', 'address2', 'laddress3', 'address3', 'laddress4', 'address4', 'lphone1', 'lphone2', 'lphone3', 'lphone4', 'lemail1', 'lemail2', 'lwebsite1', 'lwebsite2'];
        multilingualFields.forEach(field => {
          loadedData[field] = initializeMultilingualField(loadedData[field], codes);
        });
      }
      
      setData(loadedData);
    } catch {
      Swal.fire("Error loading contact data");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    try {
      await axios.put(`${config.url}/v1/dashboard/contact/${data.id}`, data, {
        headers: { Authorization: "Bearer " + cookie["token"] },
      });
      Swal.fire("Saved");
    } catch {
      Swal.fire("Error saving data");
    }
  };

  useEffect(() => {
    const initialize = async () => {
      const loadedLanguages = await loadLanguages();
      const languageCodes = loadedLanguages.length > 0
        ? loadedLanguages.map(l => l.code)
        : ['ku', 'fa', 'en', 'ar', 'tr'];
      await load(languageCodes);
    };
    initialize();
  }, []);

  // Update data when languages change to reinitialize multilingual fields
  useEffect(() => {
    if (languages.length > 0 && data) {
      const languageCodes = languages.map(l => l.code);
      const updatedData = { ...data };
      const multilingualFields = ['laddress1', 'address1', 'laddress2', 'address2', 'laddress3', 'address3', 'laddress4', 'address4', 'lphone1', 'lphone2', 'lphone3', 'lphone4', 'lemail1', 'lemail2', 'lwebsite1', 'lwebsite2'];
      multilingualFields.forEach(field => {
        updatedData[field] = initializeMultilingualField(data[field], languageCodes);
      });
      setData(updatedData);
    }
  }, [languages]);

  if (!data) return null;

  const sections = [
    {
      title: t("آدرس ها"),
      items: [
        { label: t("آدرس 1"), keyLabel: "laddress1", keyValue: "address1", pbox: "pbox1" },
        { label: t("آدرس 2"), keyLabel: "laddress2", keyValue: "address2", pbox: "pbox2" },
        { label: t("آدرس 3"), keyLabel: "laddress3", keyValue: "address3", pbox: "pbox3" },
        { label: t("آدرس 4"), keyLabel: "laddress4", keyValue: "address4", pbox: "pbox4" },
      ],
    },
    {
      title: t("شماره تماس ها"),
      items: [
        { label: t("شماره تماس  1"), keyLabel: "lphone1", keyValue: "phone1" },
        { label: t("شماره تماس 2"), keyLabel: "lphone2", keyValue: "phone2" },
        { label: t("شماره تماس 3"), keyLabel: "lphone3", keyValue: "phone3" },
        { label: t("شماره تماس 4"), keyLabel: "lphone4", keyValue: "phone4" },
      ],
    },
    {
      title: t("ایمیل ها"),
      items: [
        { label: t("ایمیل 1"), keyLabel: "lemail1", keyValue: "email1" },
        { label: t("ایمیل 2"), keyLabel: "lemail2", keyValue: "email2" },
      ],
    },
    {
      title: t("وب سایت ها"),
      items: [
        { label: t("وب سایت 1"), keyLabel: "lwebsite1", keyValue: "website1" },
        { label: t("وب سایت 2"), keyLabel: "lwebsite2", keyValue: "website2" },
      ],
    },
  ];

  return (
    <div className="bg-gray-100 min-h-[100vh] p-6" dir="rtl">
      <BreadCrump data={breadcrump} />
      <HeaderOfPage name={t("Contact Us")} />

      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-2">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => setActiveLang(lang.code)}
              className={`py-2 px-4 rounded-lg font-medium transition ${
                activeLang === lang.code
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {lang.label}
            </button>
          ))}
        </div>
        <button
          onClick={save}
          className="py-2 px-6 bg-blue-600 text-white font-medium rounded-lg shadow hover:bg-blue-700 transition"
        >
          {t("Save")}
        </button>
      </div>

      <div className="space-y-6">
        {sections.map((section) => (
          <div key={section.title} className="bg-white shadow-md rounded-xl p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4 text-right">{section.title}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {section.items.map((item: any) => (
                <div key={item.keyValue} className="space-y-2">
                  {/* Label Input */}
                  <div className="text-sm text-gray-700">{item.label} برچسب</div>
                  <input
                    type="text"
                    value={getMultilingualValue(data[item.keyLabel], activeLang)}
                    className={inputStyle}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      const currentField = data[item.keyLabel] || {};
                      const languageCodes = languages.length > 0 
                        ? languages.map(l => l.code)
                        : ['ku', 'fa', 'en', 'ar', 'tr'];
                      const baseField = typeof currentField === 'object' 
                        ? currentField
                        : initializeMultilingualField(currentField, languageCodes);
                      const updatedField = { ...baseField, [activeLang]: newValue };
                      setData({ ...data, [item.keyLabel]: updatedField });
                    }}
                  />

                  {/* Main Value */}
                  <div className="text-sm text-gray-700 mt-1">{item.label}</div>
                  {item.keyValue.includes("address") ? (
                    <textarea
                      value={getMultilingualValue(data[item.keyValue], activeLang)}
                      rows={4}
                      className={inputStyle + " resize-none"}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        const currentField = data[item.keyValue] || {};
                        const languageCodes = languages.length > 0 
                          ? languages.map(l => l.code)
                          : ['ku', 'fa', 'en', 'ar', 'tr'];
                        const baseField = typeof currentField === 'object' 
                          ? currentField
                          : initializeMultilingualField(currentField, languageCodes);
                        const updatedField = { ...baseField, [activeLang]: newValue };
                        setData({ ...data, [item.keyValue]: updatedField });
                      }}
                    />
                  ) : (
                    <input
                      type={
                        item.keyValue.includes("email") || item.keyValue.includes("website") ? "text" : "tel"
                      }
                      dir={item.keyValue.includes("phone") ? "ltr" : "auto"}
                      value={data[item.keyValue] || ""}
                      className={inputStyle}
                      onChange={(e) => setData({ ...data, [item.keyValue]: e.target.value })}
                    />
                  )}

                  {/* Postal code */}
                  {item.pbox && (
                    <>
                      <div className="text-sm text-gray-700 mt-1">کدپستی</div>
                      <input
                        type="text"
                        value={data[item.pbox] || ""}
                        className={inputStyle}
                        onChange={(e) => setData({ ...data, [item.pbox]: e.target.value })}
                      />
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
